/**
 * NFC Solutions — LAN Mesh (WebRTC data-channel transport)
 * -----------------------------------------------------------------
 *
 * Session 27.8 upgrade of the Local Coordinator interface.
 *
 * Goal
 * ----
 * Once two devices from the same restaurant know about each other
 * (via the cloud signalling relay at `WS /api/ws/mesh`), every
 * subsequent event — staff-placed orders, kitchen "ready" pings, bar
 * "ready" pings — flies **LAN-direct** over a WebRTC DataChannel.
 *
 * The cloud is used ONLY for:
 *   1. The one-time SDP/ICE handshake between peers (small, ~2 KB
 *      round-trip per peer pairing).
 *   2. Fallback delivery if a peer connection is missing OR an ACK
 *      doesn't arrive within `ACK_TIMEOUT_MS`.
 *
 * Restaurant-network operational latency drops from "seconds when
 * Wi-Fi is slow" to "milliseconds while both devices are on the same
 * router". If the ISP dies but the router stays up, existing peer
 * connections keep working — that's the whole point.
 *
 * Public surface
 * --------------
 *   initMesh({ baseUrl, token, businessId, role })
 *   destroyMesh()
 *   sendMeshEvent(envelope, { targetRole = "*" })  → { delivered, via, latencyMs }
 *   subscribeMesh(handler)                          → () => void
 *   subscribeMeshStatus(handler)                    → () => void
 *   getMeshStatus()                                 → { status, peerCount, peers }
 *
 * Every function is safe to call before init — it just no-ops or
 * returns a "not-yet-connected" result. This keeps App.js wiring
 * dead simple.
 */

import { getDeviceId } from "./device";

// ─── configuration ────────────────────────────────────────────────
// Google's public STUN server — free, no ops. Used to discover each
// peer's LAN candidates so they can hand each other IP:port pairs
// during ICE. For same-subnet devices this is often unnecessary but
// it lets the mesh gracefully cross NATs when it needs to.
// Session 27.25 — Multiple STUN servers for redundancy. Google's
// primary STUN is fastest but occasionally rate-limits or has
// regional reachability issues. Adding Cloudflare + Nextcloud STUN
// as fallbacks so at least one always answers ICE candidate probes.
// All are free public STUN endpoints (no TURN — we deliberately
// avoid TURN so P2P is genuinely LAN-direct; if STUN can't get us
// a working pair, we fall through to cloud instead of relaying via
// a paid TURN server).
const STUN_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun.cloudflare.com:3478" },
];

// How long we wait for at least one peer of the target role to ACK
// a mesh delivery before declaring the send a mesh-miss and letting
// the cloud sync loop take over.
const ACK_TIMEOUT_MS = 1000;

// Reconnect the signalling socket with exponential backoff, capped.
// Session 27.10 — tightened min from 1.5s → 800ms so a flaky Wi-Fi
// blip doesn't drop us to Cloud for 5-10s at a time.
// Session 27.29 — Aggressive initial reconnect. The mesh is
// mission-critical for LAN peer-to-peer resilience — the user's
// biggest complaint was "if the connection is lost for a couple
// seconds, everything stops". Reducing the first backoff from
// 800ms → 200ms means a signalling drop reconnects in ~250ms
// (200 + jitter) on the first attempt, which is imperceptible
// to a user placing an order. Subsequent failures still ramp up
// to a 15s ceiling to avoid a stampede when signalling is truly
// down for extended periods.
const SIGNAL_RECONNECT_MIN_MS = 200;
const SIGNAL_RECONNECT_MAX_MS = 15000;

// Keepalive on the signalling socket. Cloudflare + mobile carriers
// idle-kill anything > ~60s so we ping every 20s to keep the tunnel
// warm even when there's no application traffic.
const SIGNAL_PING_MS = 20000;

// Session 27.10 — Data channel liveness probe. Each connected peer
// gets a keepalive ping every N ms; if we don't hear back within
// PROBE_TIMEOUT_MS we assume the tunnel is gone and trigger an ICE
// restart. This is what stops the "we say LAN but the peer already
// vanished" ghost that made the pill flip back to Cloud silently.
const PEER_PROBE_MS = 5000;
const PEER_PROBE_TIMEOUT_MS = 8000;

// ─── module singleton state ───────────────────────────────────────
const STATE = {
  armed: false,
  destroyed: false,
  baseUrl: null,
  token: null,
  businessId: null,
  role: "staff",
  deviceLabel: null,
  peerId: null,
  socket: null,
  socketReconnectAttempt: 0,
  socketReconnectTimer: null,
  socketPingTimer: null,
  // peer_id → { role, device_label, pc: RTCPeerConnection,
  //             channel: RTCDataChannel, ready: boolean,
  //             initiator: boolean, pendingIce: [] }
  peers: new Map(),
  // change_id → { resolve, timer, targetRole }
  pending: new Map(),
  // Fan-out handlers for incoming remote events (envelope handler).
  eventHandlers: new Set(),
  // Fan-out handlers for status changes (fires on peer join/leave/socket up/down).
  statusHandlers: new Set(),
};

// ─── logging helper ───────────────────────────────────────────────
function log(...args) {
  try {
    // Keep quiet in production consoles but leave a hook for
    // Developer Tools mesh diagnostics.
    if (typeof window !== "undefined" && window.__NFCS_MESH_DEBUG) {
      // eslint-disable-next-line no-console
      console.log("[lanMesh]", ...args);
    }
  } catch (_) { /* noop */ }
}

// ─── status broadcasting ──────────────────────────────────────────
function _emitStatus() {
  const snapshot = getMeshStatus();
  STATE.statusHandlers.forEach((fn) => {
    try { fn(snapshot); } catch (e) { console.warn("[lanMesh] status handler error:", e?.message || e); }
  });
}

export function getMeshStatus() {
  const readyPeers = [];
  const announcedPeers = [];
  STATE.peers.forEach((p, id) => {
    const entry = {
      peer_id: id,
      role: p.role || null,
      device_label: p.deviceLabel || null,
      ready: !!p.ready,
    };
    announcedPeers.push(entry);
    if (p.ready) readyPeers.push(entry);
  });
  let status;
  if (!STATE.armed) status = "idle";
  else if (readyPeers.length > 0) status = "lan";
  else if (STATE.socket && STATE.socket.readyState === WebSocket.OPEN) status = "cloud";
  else status = "offline";
  // Session 27.96 — LAN latch (sticky-LAN).
  //
  // Operator complaint: "LAN → Cloud → LAN → Cloud fluctuation is
  // causing instability." Root cause: a single dropped WebRTC data
  // channel (which can happen during any 100 ms network blip) makes
  // `readyPeers.length` momentarily go to 0. The status pill flips
  // to "cloud", downstream code treats that as a routing signal,
  // then WebRTC re-handshakes 200 ms later and we're back on LAN.
  // The oscillation confuses `configureCoordinatorMesh` and every
  // consumer that reads `getMeshStatus`.
  //
  // Fix: once we've reached "lan" status, latch it. Subsequent
  // reports that would say "cloud" are treated as still LAN for
  // 60 s — long enough for any WebRTC re-handshake to complete.
  // Only demote to "cloud" if we've been below the ready threshold
  // for the full 60 s. "offline" (nothing connected at all) still
  // flips immediately so operators know when the local network
  // truly died.
  try {
    if (!STATE._lanLatch) STATE._lanLatch = { at: 0, status: null };
    const nowT = Date.now();
    if (status === "lan") {
      STATE._lanLatch = { at: nowT, status: "lan" };
      // Session 30.8 — remember that this session DID reach LAN so
      // the UI can tell "LAN lost" (hide Refresh, offer controlled
      // reconnect) apart from "never had peers" (single-device site).
      STATE._everLan = true;
    } else if (status === "cloud"
               && STATE._lanLatch.status === "lan"
               && (nowT - STATE._lanLatch.at) < 60000) {
      // Suppress the momentary drop — pretend we're still on LAN.
      status = "lan";
    } else if (status === "offline") {
      STATE._lanLatch = { at: 0, status: null };
    }
  } catch (_) { /* introspection best-effort */ }
  return {
    status,
    peerCount: readyPeers.length,
    // Session 30.8 — true when the mesh had ready peers earlier in
    // this armed session but has none now (after the 60 s latch).
    lanLost: Boolean(STATE.armed && STATE._everLan && status !== "lan"),
    everLan: Boolean(STATE._everLan),
    // Session 27.73 — Also expose the total "announced" peers so the
    // status popover can distinguish "we see peers, WebRTC is still
    // handshaking" (announced > ready) from "we truly are alone on
    // the network" (announced === 0). This is what turns a mysterious
    // "Cloud" pill into "3 devices announced, 0 handshakes complete".
    announcedCount: announcedPeers.length,
    peers: readyPeers,
    announcedPeers,
    self: {
      peer_id: STATE.peerId,
      role: STATE.role,
      device_label: STATE.deviceLabel || null,
    },
  };
}

export function subscribeMeshStatus(handler) {
  if (typeof handler !== "function") return () => {};
  STATE.statusHandlers.add(handler);
  try { handler(getMeshStatus()); } catch (e) { console.warn("[lanMesh] initial status handler error:", e?.message || e); }
  return () => STATE.statusHandlers.delete(handler);
}

export function subscribeMesh(handler) {
  if (typeof handler !== "function") return () => {};
  STATE.eventHandlers.add(handler);
  return () => STATE.eventHandlers.delete(handler);
}

// ─── init / teardown ──────────────────────────────────────────────
export function initMesh({ baseUrl, token, businessId, role = "staff", deviceLabel = null } = {}) {
  if (!baseUrl || !token || !businessId) return false;
  // Same identity? Nothing to do.
  if (
    STATE.armed &&
    STATE.baseUrl === baseUrl &&
    STATE.token === token &&
    STATE.businessId === businessId &&
    STATE.role === role &&
    STATE.deviceLabel === deviceLabel
  ) {
    return true;
  }
  destroyMesh();
  STATE.destroyed = false;
  STATE.armed = true;
  STATE.baseUrl = baseUrl.replace(/\/+$/, "");
  STATE.token = token;
  STATE.businessId = businessId;
  STATE.role = String(role || "staff").toLowerCase();
  // Session 27.73 — Human-readable label for this device (e.g.
  // "iPad · Staff", "Windows PC · Owner"). Exchanged over the data
  // channel via a "hello" message so peers can render each other's
  // labels in the LAN status popover instead of raw UUIDs.
  STATE.deviceLabel = deviceLabel || null;
  try { STATE.peerId = getDeviceId(); } catch (_) { STATE.peerId = _fallbackPeerId(); }
  if (!STATE.peerId) STATE.peerId = _fallbackPeerId();
  _openSignalSocket();
  _emitStatus();
  // Session 27.78 — Start polling the LAN signalling inbox so peers
  // can complete WebRTC handshakes even when the cloud WS is down.
  _startLanSignalPoller();
  // Session 30.26 — Turn discovered/cached LAN peers into pairings
  // when the cloud can't introduce us (router-only operation).
  _startLanDiscoveryLoop();
  // Session 30.26 — Re-offer / nudge stuck handshakes (lost offers).
  _startHandshakeWatchdog();
  // Session 27.10 — when the tab/PWA becomes visible again after
  // being backgrounded (screen lock, app switch), immediately verify
  // the signalling socket is still healthy. Mobile OSes aggressively
  // close inactive sockets and WebRTC can silently stall.
  if (typeof document !== "undefined" && !STATE._visibilityBound) {
    STATE._visibilityBound = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      if (!STATE.armed) return;
      const sock = STATE.socket;
      if (!sock || sock.readyState !== WebSocket.OPEN) {
        if (STATE.socketReconnectTimer) clearTimeout(STATE.socketReconnectTimer);
        STATE.socketReconnectTimer = null;
        STATE.socketReconnectAttempt = 0;
        _openSignalSocket();
      } else {
        // Force a small ping so we know the tunnel is alive; if it's
        // dead the socket will error/close and we'll reconnect.
        try { sock.send("ping"); } catch (_) { /* noop */ }
      }
      // Poke each peer with a probe so any stale connection gets
      // healed via ICE restart quickly.
      STATE.peers.forEach((p, id) => {
        if (p.channel && p.channel.readyState === "open") {
          try { p.channel.send(JSON.stringify({ type: "ping", ts: Date.now() })); }
          catch (_) { /* noop */ }
        } else if (p.initiator) {
          _tryIceRestart(id, p).catch(() => { /* noop */ });
        }
      });
    });
  }
  return true;
}

// Session 27.25 — Expose a diagnostic snapshot on window so operators
// can open DevTools on a live tablet and immediately see WHY the mesh
// isn't pairing. Zero performance cost; only called on demand.
//
//   window.__nfcsMeshDebug()           → returns detailed snapshot
//   window.__nfcsMeshEnableDebug()     → turns on verbose console logs
//
// Returns { self, socket, peers[] } including per-peer ICE state,
// connection state, data-channel state, initiator role, and pending
// ICE queue depth. This is the fastest way to diagnose "shows Cloud
// on both phones even though they're on the same Wi-Fi".
if (typeof window !== "undefined") {
  window.__nfcsMeshDebug = async () => {
    const peers = [];
    const promises = [];
    STATE.peers.forEach((p, id) => {
      const promise = (async () => {
        let iceStats = null;
        // Session 27.26 — Also collect ICE candidate types (host /
        // srflx / relay / prflx) so we can pinpoint mDNS/STUN issues.
        // If we only ever see "host" candidates but the pair never
        // connects, the two devices likely can't resolve each
        // other's mDNS names (common on mobile browsers over WiFi).
        if (p.pc && typeof p.pc.getStats === "function") {
          try {
            const stats = await p.pc.getStats();
            const local = { host: 0, srflx: 0, relay: 0, prflx: 0 };
            const remote = { host: 0, srflx: 0, relay: 0, prflx: 0 };
            let selectedPair = null;
            stats.forEach((r) => {
              if (r.type === "local-candidate" && r.candidateType) local[r.candidateType] = (local[r.candidateType] || 0) + 1;
              if (r.type === "remote-candidate" && r.candidateType) remote[r.candidateType] = (remote[r.candidateType] || 0) + 1;
              if (r.type === "candidate-pair" && r.nominated && r.selected) selectedPair = { local: r.localCandidateId, remote: r.remoteCandidateId, state: r.state };
            });
            iceStats = { local, remote, selectedPair };
          } catch (_) { /* noop */ }
        }
        peers.push({
          peer_id: id,
          role: p.role,
          initiator: p.initiator,
          connection_state: p.pc?.connectionState || null,
          ice_state: p.pc?.iceConnectionState || null,
          ice_gathering_state: p.pc?.iceGatheringState || null,
          signalling_state: p.pc?.signalingState || null,
          channel_state: p.channel?.readyState || null,
          pending_ice_count: (p.pendingIce || []).length,
          probe_misses: p.probeMisses || 0,
          ready: !!p.ready,
          ice_stats: iceStats,
        });
      })();
      promises.push(promise);
    });
    await Promise.all(promises);
    return {
      self: { peer_id: STATE.peerId, role: STATE.role, business_id: STATE.businessId },
      armed: !!STATE.armed,
      destroyed: !!STATE.destroyed,
      socket: {
        state: STATE.socket?.readyState ?? null,
        state_label: ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][STATE.socket?.readyState] || "NONE",
        reconnect_attempts: STATE.socketReconnectAttempt || 0,
      },
      peers,
      counts: { peers: peers.length, ready_peers: peers.filter((p) => p.ready).length },
    };
  };
  window.__nfcsMeshEnableDebug = () => {
    window.__NFCS_MESH_DEBUG = true;
    console.log("[lanMesh] Verbose logging enabled. Reload the page to see the full handshake flow.");
  };

  // Session 27.27 — Human-readable one-liner explanation of the
  // mesh state. Prints a colour-coded summary directly to the
  // console so operators don't have to parse the JSON snapshot.
  //
  //   window.__nfcsMeshExplain()
  //
  // Example output:
  //   ┌─────────────────────────────────────────────
  //   │ Mesh status:  cloud  (0 peers)
  //   │ Socket:       OPEN
  //   │ My peer id:   abc-123
  //   │ My role:      staff
  //   │
  //   │ ⚠  No peers in the room. Either:
  //   │   • no other tablets in this business are online, or
  //   │   • the signalling backend didn't broadcast peer_joined.
  //   └─────────────────────────────────────────────
  window.__nfcsMeshExplain = async () => {
    const snap = await window.__nfcsMeshDebug();
    const lines = [];
    const status = snap.counts.ready_peers > 0
      ? `🟢 LAN (${snap.counts.ready_peers} peer${snap.counts.ready_peers === 1 ? "" : "s"})`
      : snap.socket.state_label === "OPEN"
        ? "🟡 CLOUD (no LAN peers paired)"
        : "🔴 OFFLINE (signalling socket down)";
    lines.push("┌─────────────────────────────────────────────");
    lines.push(`│ Mesh status:  ${status}`);
    lines.push(`│ Socket:       ${snap.socket.state_label}`);
    lines.push(`│ My peer id:   ${snap.self.peer_id}`);
    lines.push(`│ My role:      ${snap.self.role}`);
    lines.push(`│ Business:     ${snap.self.business_id}`);
    lines.push("│");
    if (snap.peers.length === 0) {
      lines.push("│ ⚠  No peers in the room.");
      lines.push("│    • Are other tablets in the SAME business signed in?");
      lines.push("│    • Signalling should broadcast peer_joined when they arrive.");
      lines.push("│    • Try refreshing the OTHER device.");
    } else {
      lines.push(`│ Peers seen: ${snap.peers.length}`);
      snap.peers.forEach((p, i) => {
        const emoji = p.channel_state === "open" ? "🟢" :
                      p.connection_state === "failed" ? "🔴" :
                      p.connection_state === "connecting" ? "🟡" : "⚪";
        lines.push(`│   ${emoji} peer #${i + 1}: ${p.peer_id.slice(0, 8)}… (${p.role})`);
        lines.push(`│      pc: ${p.connection_state}  ice: ${p.ice_state}  channel: ${p.channel_state || "none"}`);
        if (p.ice_stats) {
          const l = p.ice_stats.local;
          const r = p.ice_stats.remote;
          lines.push(`│      local candidates:  host=${l.host} srflx=${l.srflx} relay=${l.relay}`);
          lines.push(`│      remote candidates: host=${r.host} srflx=${r.srflx} relay=${r.relay}`);
          if (!p.ice_stats.selectedPair) {
            lines.push("│      ⚠  No candidate pair selected — ICE couldn't find a working path.");
            lines.push("│         Common cause: mobile hotspot AP isolation, corporate firewall, or mDNS blocked.");
          }
        }
      });
    }
    lines.push("└─────────────────────────────────────────────");
    // eslint-disable-next-line no-console
    console.log(lines.join("\n"));
    return snap;
  };
}

export function destroyMesh() {
  STATE.destroyed = true;
  STATE.armed = false;
  STATE._everLan = false;
  if (STATE.socketReconnectTimer) { clearTimeout(STATE.socketReconnectTimer); STATE.socketReconnectTimer = null; }
  if (STATE.socketPingTimer) { clearInterval(STATE.socketPingTimer); STATE.socketPingTimer = null; }
  _stopLanSignalPoller();
  _stopLanDiscoveryLoop();
  _stopHandshakeWatchdog();
  if (STATE.socket) {
    try { STATE.socket.close(1000, "client-detach"); } catch (_) { /* noop */ }
    STATE.socket = null;
  }
  STATE.peers.forEach((p) => {
    _stopPeerProbe(p);
    try { if (p.channel) p.channel.close(); } catch (_) { /* noop */ }
    try { if (p.pc) p.pc.close(); } catch (_) { /* noop */ }
  });
  STATE.peers.clear();
  STATE.pending.forEach(({ timer }) => { try { clearTimeout(timer); } catch (_) {} });
  STATE.pending.clear();
  _emitStatus();
}

/**
 * Session 30.8 — Controlled LAN reconnect.
 *
 * The ONLY sanctioned way to rebuild the mesh from UI. Refresh
 * buttons must never call `destroyMesh()` directly any more: when
 * peers are paired ("lan") a rebuild would drop every data channel
 * and force a full re-handshake for no benefit. This helper refuses
 * to tear down a healthy mesh unless `force` is passed, and returns
 * what it did so the caller can toast accordingly.
 *
 * After teardown the App.js sync loop notices `status === "idle"`
 * on its next tick (<= 1.5 s) and calls `initMesh` again with the
 * current session — that is the "reconnect".
 */
export function reconnectMesh({ force = false } = {}) {
  const snap = getMeshStatus();
  if (!force && snap.status === "lan" && (snap.peerCount || 0) > 0) {
    return { rebuilt: false, reason: "lan-healthy", status: snap.status };
  }
  const identity = {
    baseUrl: STATE.baseUrl, token: STATE.token, businessId: STATE.businessId,
    role: STATE.role, deviceLabel: STATE.deviceLabel,
  };
  destroyMesh();
  // Re-arm immediately when we still know who we are, instead of
  // waiting for the sync loop, so the pill flips to "pairing" fast.
  if (identity.baseUrl && identity.token && identity.businessId) {
    try { initMesh(identity); } catch (_) { /* sync loop will retry */ }
  }
  return { rebuilt: true, reason: force ? "forced" : "lan-down", status: snap.status };
}

function _fallbackPeerId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch (_) { /* noop */ }
  return `peer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── signalling socket ────────────────────────────────────────────
function _openSignalSocket() {
  if (STATE.destroyed) return;
  if (!STATE.baseUrl || !STATE.token) return;
  let url;
  try {
    const u = new URL(STATE.baseUrl);
    const scheme = u.protocol === "https:" ? "wss:" : "ws:";
    const params = new URLSearchParams({
      token: STATE.token,
      peer_id: STATE.peerId,
      role: STATE.role,
    });
    url = `${scheme}//${u.host}/api/ws/mesh?${params.toString()}`;
  } catch (_) { return; }

  let sock;
  try { sock = new WebSocket(url); } catch (_) { _scheduleSignalReconnect(); return; }
  STATE.socket = sock;

  sock.onopen = () => {
    STATE.socketReconnectAttempt = 0;
    if (STATE.socketPingTimer) clearInterval(STATE.socketPingTimer);
    STATE.socketPingTimer = setInterval(() => {
      try { sock.send("ping"); } catch (_) { /* noop */ }
    }, SIGNAL_PING_MS);
    _emitStatus();
  };

  sock.onmessage = async (ev) => {
    if (ev.data === "pong") return;
    let msg;
    try { msg = JSON.parse(ev.data); } catch (_) { return; }
    if (!msg || typeof msg.type !== "string") return;
    log("signal <-", msg.type, msg.from || msg.peer_id || "");
    try { await _handleSignal(msg); } catch (e) { log("signal handler error", e); }
  };

  sock.onerror = () => { /* onclose will handle reconnect */ };
  sock.onclose = () => {
    if (STATE.socketPingTimer) { clearInterval(STATE.socketPingTimer); STATE.socketPingTimer = null; }
    STATE.socket = null;
    // Session 27.22 — DELIBERATELY do NOT tear down peer connections
    // here. The signalling socket is only required for the initial
    // SDP/ICE handshake between two peers; once a WebRTC DataChannel
    // is established the two devices talk directly over LAN and the
    // cloud is irrelevant. Previous behaviour dropped every peer on
    // signalling close, which meant the moment the ISP died (or the
    // preview's Cloudflare tunnel hiccupped) the entire mesh
    // collapsed — the exact opposite of the header-comment promise
    // "If the ISP dies but the router stays up, existing peer
    // connections keep working — that's the whole point."
    //
    // Peers now stay connected until:
    //   • the peer's WebRTC connection itself reports "failed" or
    //     "closed" (see the RTCPeerConnection.oniceconnectionstatechange
    //     handler in _ensurePeer), or
    //   • we receive a `peer_left` signal from the server (peer
    //     legitimately left), or
    //   • destroyMesh() is called (logout / role change).
    //
    // If we reconnect to signalling and the same peers are still
    // there, `_handleSignal("roster")` will treat them as known
    // (`_ensurePeer` is idempotent) and NOT re-handshake — so LAN
    // stays hot across signalling outages.
    _emitStatus();
    if (!STATE.destroyed) _scheduleSignalReconnect();
  };
}

function _scheduleSignalReconnect() {
  if (STATE.destroyed) return;
  if (STATE.socketReconnectTimer) return;
  STATE.socketReconnectAttempt += 1;
  const backoff = Math.min(
    SIGNAL_RECONNECT_MAX_MS,
    SIGNAL_RECONNECT_MIN_MS * Math.pow(1.6, STATE.socketReconnectAttempt - 1),
  );
  const jitter = Math.floor(Math.random() * 400);
  STATE.socketReconnectTimer = setTimeout(() => {
    STATE.socketReconnectTimer = null;
    _openSignalSocket();
  }, backoff + jitter);
}

function _sendSignal(payload) {
  // Session 30.26 — Every signal carries our business + role so a
  // LAN-relayed envelope can be scoped/labelled by the receiver (the
  // cloud relay strips unknown fields and is business-scoped anyway).
  if (payload && typeof payload === "object") {
    if (STATE.businessId && !payload.business_id) payload.business_id = String(STATE.businessId);
    if (STATE.role && !payload.role) payload.role = STATE.role;
    if (STATE.peerId && !payload.from) payload.from = STATE.peerId;
  }
  const sock = STATE.socket;
  let sentCloud = false;
  if (sock && sock.readyState === WebSocket.OPEN) {
    try { sock.send(JSON.stringify(payload)); sentCloud = true; } catch (_) { /* fall through */ }
  }
  // Session 27.78 — LAN signalling fallback. If the cloud WS is down
  // but we know a coordinator's LAN address (via mDNS or the peer
  // cache), route this signalling envelope over LAN via the Tauri-
  // hosted HTTP relay. Fire-and-forget so callers don't have to await.
  //
  // Session 30.34 — MIXED-MODE FIX: send targeted signals over BOTH
  // paths, not just when our own cloud socket is down. Real-world case
  // from the router-only test: the Staff phone still had mobile data
  // (cloud socket open) while the Kitchen/Bar tablets had no internet.
  // Every offer/answer/ICE for them went to the cloud only — which they
  // could never receive — so the phone sat on "pairing…" / "LAN lost"
  // until someone pressed Reconnect. The LAN mailbox is a silent no-op
  // when the target has no known LAN address, and receivers dedupe the
  // duplicate that arrives via both paths (see _handleSignal), so
  // always mirroring costs a few hundred bytes per handshake message.
  try {
    if (!payload || !payload.to) return sentCloud;
    _sendSignalOverLan(payload).catch(() => {});
    return true;
  } catch (_) { return sentCloud; }
}

// Best-effort LAN signalling send. Silent no-op if the platform has no
// native transport (browser / preview). Import dynamically so the mesh
// module stays browser-safe.
async function _sendSignalOverLan(payload) {
  try {
    if (typeof window === "undefined") return;
    const sigMod = await import("./lanSignalling");
    if (!STATE.peerId) return;
    const signaller = sigMod.getSharedSignaller(STATE.peerId);
    if (!signaller.tauri && !signaller.capacitor) {
      const info = await signaller.detect();
      if (!info.tauri && !info.capacitor) return;
    }
    await signaller.send({
      to: payload.to,
      kind: payload.type || "signal",
      payload: JSON.stringify(payload),
    });
  } catch (_) { /* noop */ }
}

// Session 27.78 — LAN signalling poll. When we can't reach the cloud
// WS, drain the Tauri inbox periodically and inject any received
// envelopes into `_handleSignal` as though they came from the cloud.
// Started once from `initMesh` and cancelled from `destroyMesh`.
function _startLanSignalPoller() {
  if (STATE.lanPollTimer) return;
  let tick = 0;
  STATE.lanPollTimer = setInterval(async () => {
    if (STATE.destroyed) return;
    tick += 1;
    // Session 30.26 — The poller now also fetches remote mailboxes over
    // HTTP; while the cloud socket is healthy that is only a safety
    // net, so relax to every 4th tick (6 s) to keep LAN chatter low.
    const socketOpen = !!(STATE.socket && STATE.socket.readyState === WebSocket.OPEN);
    // Session 30.34 — While ANY peer is still pairing, drain the LAN
    // mailbox at full speed even if our cloud socket is open: in mixed
    // mode (we have internet, the peer does not) the peer's answer/ICE
    // can only reach us through this mailbox, and a 6 s cadence made a
    // 3-step handshake take ~20 s.
    let pairing = false;
    try {
      for (const p of STATE.peers.values()) {
        if (p && !p.ready) { pairing = true; break; }
      }
    } catch (_) { /* noop */ }
    if (socketOpen && !pairing && tick % 4 !== 0) return;
    try {
      const sigMod = await import("./lanSignalling");
      if (!STATE.peerId) return;
      const signaller = sigMod.getSharedSignaller(STATE.peerId);
      if (!signaller.tauri && !signaller.capacitor) return;
      const msgs = await signaller.drainInbox();
      for (const m of msgs) {
        // The `payload` field carries our original signalling JSON.
        try {
          const inner = JSON.parse(m.payload);
          if (inner && !inner.from && m.from) inner.from = m.from;
          _handleSignal(inner);
        } catch (_) { /* noop */ }
      }
    } catch (_) { /* noop */ }
  }, 1500);
}

function _stopLanSignalPoller() {
  if (STATE.lanPollTimer) {
    try { clearInterval(STATE.lanPollTimer); } catch (_) { /* noop */ }
    STATE.lanPollTimer = null;
  }
}

// ─── signalling message handlers ──────────────────────────────────
// Session 27.22 — Helper: is this peer's WebRTC connection already
// established / actively healing itself? If so, a new offer would
// force an unnecessary re-negotiation and briefly interrupt the LAN
// data channel. Only handshake to peers we don't already know or
// whose connection has actually failed.
//
// Session 27.32 — FIX regression from 27.25: the previous version
// also returned true for "new" state. But "new" means the RTCPeer
// Connection object EXISTS but has NEVER STARTED signalling — that
// is EXACTLY when we need to initiate. Treating "new" as "alive"
// silently blocked every first-time handshake, which is why yesterday
// showed peer-to-peer LAN but today never established one. Now we
// only treat "connecting" or "connected" as alive; "new" always
// falls through and initiates.
function _peerAlreadyLive(peerId) {
  const existing = STATE.peers.get(peerId);
  if (!existing || !existing.pc) return false;
  const s = existing.pc.connectionState;
  // Also require that the peer already has a data channel — if
  // there's no channel yet we're not really "live" regardless of the
  // pc state (this catches a race where the offerer created its
  // pc object but hasn't yet emitted the offer).
  if (!existing.channel && !existing.pendingChannel) return false;
  return ["connecting", "connected"].includes(s);
}

async function _handleSignal(msg) {
  switch (msg.type) {
    case "roster": {
      // Seed peers already in the room. For every peer whose id
      // sorts LESS than ours we let *them* initiate (deterministic
      // glare avoidance). We create the RTCPeerConnection either
      // way so their incoming offer finds a home.
      const peers = Array.isArray(msg.peers) ? msg.peers : [];
      for (const p of peers) {
        _ensurePeer(p.peer_id, p.role);
        // Session 27.22 — Skip re-handshake for peers that are
        // already live. Keeps the LAN channel warm across
        // signalling-socket reconnects (ISP hiccups, mobile
        // handoffs) so the "handshake stays solid" contract holds.
        if (_shouldInitiate(p.peer_id) && !_peerAlreadyLive(p.peer_id)) {
          await _makeOfferTo(p.peer_id);
        }
      }
      _emitStatus();
      break;
    }
    case "peer_joined": {
      _ensurePeer(msg.peer_id, msg.role);
      if (_shouldInitiate(msg.peer_id) && !_peerAlreadyLive(msg.peer_id)) {
        await _makeOfferTo(msg.peer_id);
      }
      _emitStatus();
      break;
    }
    case "peer_left": {
      const p = STATE.peers.get(msg.peer_id);
      if (p) {
        // Session 30.26 — `peer_left` only means the peer's CLOUD
        // signalling socket closed (its internet blipped, Cloudflare
        // hiccupped, screen locked…). If our direct LAN data channel to
        // it is still open, tearing it down here is exactly the
        // LAN→Cloud→LAN flapping operators reported. Keep a healthy
        // channel; the per-peer probe (ping/pong) will notice a truly
        // dead peer and the ICE-restart / failed path removes it.
        const channelOpen = !!(p.channel && p.channel.readyState === "open" && p.ready);
        if (channelOpen) {
          p.leftSignallingAt = Date.now();
          log("peer_left ignored — LAN channel still open", msg.peer_id);
        } else {
          _stopPeerProbe(p);
          try { p.channel && p.channel.close(); } catch (_) {}
          try { p.pc && p.pc.close(); } catch (_) {}
          STATE.peers.delete(msg.peer_id);
        }
      }
      _emitStatus();
      break;
    }
    case "offer": {
      // Session 30.26 — Cross-business guard for LAN-relayed signals
      // (the cloud relay is already business-scoped server-side).
      if (msg.business_id && STATE.businessId && String(msg.business_id) !== String(STATE.businessId)) return;
      const peer = _ensurePeer(msg.from, msg.role || undefined);
      // Session 30.34 — The same offer now arrives via cloud AND LAN
      // mailbox. Applying it twice throws ("wrong state") and stalls
      // the handshake, so ignore an exact repeat of the last offer.
      if (peer.lastRemoteOfferSdp && peer.lastRemoteOfferSdp === msg.sdp) {
        log("duplicate offer ignored", msg.from);
        return;
      }
      peer.lastRemoteOfferSdp = msg.sdp;
      await peer.pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
      // Drain any ICE that arrived before the offer.
      await _flushPendingIce(peer);
      const answer = await peer.pc.createAnswer();
      await peer.pc.setLocalDescription(answer);
      _sendSignal({ type: "answer", to: msg.from, sdp: peer.pc.localDescription.sdp });
      break;
    }
    case "answer": {
      if (msg.business_id && STATE.businessId && String(msg.business_id) !== String(STATE.businessId)) return;
      const peer = STATE.peers.get(msg.from);
      if (!peer) return;
      // Ignore a stray answer when we're not expecting one (e.g. a
      // duplicate relayed via both cloud and LAN) — applying it would
      // throw and leave the connection in a stuck state.
      if (peer.pc.signalingState !== "have-local-offer") return;
      await peer.pc.setRemoteDescription({ type: "answer", sdp: msg.sdp });
      await _flushPendingIce(peer);
      break;
    }
    case "renegotiate": {
      // Session 30.26 — The receiver never got our offer (lost / stale
      // socket). Re-offer straight away with an ICE restart.
      if (msg.business_id && STATE.businessId && String(msg.business_id) !== String(STATE.businessId)) return;
      const peer = _ensurePeer(msg.from, msg.role || undefined);
      if (!peer.initiator) return; // they should be offering, not us
      // Session 30.34 — dual-path duplicates: one re-offer per 3 s is plenty.
      if (peer.lastRenegotiateAt && Date.now() - peer.lastRenegotiateAt < 3000) return;
      peer.lastRenegotiateAt = Date.now();
      peer.lastOfferAt = 0; // bypass the throttle — this is an explicit request
      if (peer.pc.signalingState === "have-local-offer") {
        await _tryIceRestart(msg.from, peer);
      } else {
        await _makeOfferTo(msg.from);
      }
      break;
    }
    case "ice": {
      if (msg.business_id && STATE.businessId && String(msg.business_id) !== String(STATE.businessId)) return;
      const peer = STATE.peers.get(msg.from);
      if (!peer || !msg.candidate) return;
      // If the remote description isn't set yet, buffer the ICE.
      if (peer.pc.remoteDescription && peer.pc.remoteDescription.type) {
        try { await peer.pc.addIceCandidate(msg.candidate); } catch (_) { /* noop */ }
      } else {
        peer.pendingIce.push(msg.candidate);
      }
      break;
    }
    default:
      break;
  }
}

async function _flushPendingIce(peer) {
  while (peer.pendingIce.length) {
    const c = peer.pendingIce.shift();
    try { await peer.pc.addIceCandidate(c); } catch (_) { /* noop */ }
  }
}

function _shouldInitiate(otherPeerId) {
  // Deterministic tie-break: whichever peer_id string sorts smaller
  // is the offerer. This prevents "glare" where both sides send
  // offers at the same time.
  return String(STATE.peerId) < String(otherPeerId);
}

// ─── per-peer RTCPeerConnection wiring ────────────────────────────
function _ensurePeer(peerId, role) {
  if (STATE.peers.has(peerId)) {
    const existing = STATE.peers.get(peerId);
    // Session 30.26 — A closed RTCPeerConnection can never be reused
    // (createOffer/setRemoteDescription throw). Previously the dead
    // record lingered forever, so a peer that dropped and came back
    // could not re-pair until logout. Recreate it transparently.
    const dead = !existing.pc
      || existing.pc.connectionState === "closed"
      || existing.pc.signalingState === "closed";
    if (!dead) {
      if (role && !existing.role) existing.role = role;
      return existing;
    }
    _stopPeerProbe(existing);
    try { existing.channel && existing.channel.close(); } catch (_) { /* noop */ }
    try { existing.pc && existing.pc.close(); } catch (_) { /* noop */ }
    STATE.peers.delete(peerId);
    if (!role) role = existing.role || null;
  }
  const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });
  const record = {
    role: role || null,
    deviceLabel: null,
    pc,
    channel: null,
    ready: false,
    initiator: _shouldInitiate(peerId),
    pendingIce: [],
    createdAt: Date.now(),
    lastOfferAt: 0,
    reofferCount: 0,
  };
  STATE.peers.set(peerId, record);

  pc.onicecandidate = (ev) => {
    if (ev.candidate) {
      _sendSignal({ type: "ice", to: peerId, candidate: ev.candidate.toJSON ? ev.candidate.toJSON() : ev.candidate });
    }
  };
  pc.onconnectionstatechange = () => {
    log("pc state", peerId, pc.connectionState);
    if (pc.connectionState === "failed") {
      // Session 27.10 — attempt an ICE restart before tearing down.
      // On mobile the connection often "fails" during a wifi handoff
      // or when the screen locks briefly; an ICE restart re-uses the
      // existing signalling channel to swap fresh candidates without
      // dropping application state.
      record.ready = false;
      record.failedAt = Date.now();
      _tryIceRestart(peerId, record).catch(() => {
        record.ready = false;
        _emitStatus();
      });
      _emitStatus();
    } else if (pc.connectionState === "disconnected") {
      record.ready = false;
      _emitStatus();
      // Session 30.26 — Don't wait for two 8 s probe misses: if we are
      // the initiator and the link is still "disconnected" 3 s from
      // now, restart ICE straight away (router reboot, AP roam…).
      setTimeout(() => {
        if (pc.connectionState === "disconnected" && record.initiator) {
          _tryIceRestart(peerId, record).catch(() => { /* noop */ });
        }
      }, 3000);
    } else if (pc.connectionState === "closed") {
      record.ready = false;
      _emitStatus();
    } else if (pc.connectionState === "connected") {
      // Reset the probe miss counter — the tunnel is healthy again.
      record.probeMisses = 0;
      record.failedAt = 0;
      _emitStatus();
    }
  };
  pc.oniceconnectionstatechange = () => {
    // Extra safety net: iceConnectionState reports transitions the
    // general connectionState sometimes misses on Safari.
    if (pc.iceConnectionState === "failed") {
      _tryIceRestart(peerId, record).catch(() => { /* noop */ });
    }
  };
  pc.ondatachannel = (ev) => {
    _bindChannel(peerId, record, ev.channel);
  };
  return record;
}

async function _makeOfferTo(peerId) {
  const peer = STATE.peers.get(peerId);
  if (!peer) return;
  // Session 30.26 — Offer throttle. The cloud roster and the LAN
  // discovery pass can both decide to initiate within the same second;
  // a second offer while the first is outstanding would renegotiate
  // and stall the handshake. One offer per peer per 6 s is plenty.
  const now = Date.now();
  if (peer.lastOfferAt && now - peer.lastOfferAt < 6000) return;
  if (peer.pc.signalingState === "have-local-offer" && peer.lastOfferAt && now - peer.lastOfferAt < 15000) return;
  peer.lastOfferAt = now;
  // Initiator creates the data channel BEFORE createOffer so it lands
  // in the SDP.
  if (!peer.channel) {
    const ch = peer.pc.createDataChannel("events", { ordered: true });
    _bindChannel(peerId, peer, ch);
  }
  const offer = await peer.pc.createOffer();
  await peer.pc.setLocalDescription(offer);
  _sendSignal({ type: "offer", to: peerId, sdp: peer.pc.localDescription.sdp });
}

// ─── Session 30.26 — LAN discovery → pairing ──────────────────────
// Until now a WebRTC handshake could ONLY start from a cloud
// `roster` / `peer_joined` signal. mDNS discovery merely filled a
// cache. Consequence: with the internet down, a tablet that restarts
// (or a new one switched on) never pairs with anyone — even though
// every peer is one hop away on the router. This pass turns the
// discovered/cached peers into real pairings:
//   • every 4 s while the cloud signalling socket is NOT open
//     (and every 20 s while it is, as a belt-and-braces catch-up),
//   • for each known LAN device that is not ours and not already live,
//     create the RTCPeerConnection and — if we are the deterministic
//     initiator — send an offer. The offer travels over the LAN
//     signalling mailbox (see lanSignalling.js) when the cloud is down.
// Peers we learn about this way are limited to the same business
// (both the mDNS record and the peer cache are business-tagged when
// available; untagged legacy records are still accepted since the
// signalling envelope itself carries `business_id` and the receiver
// drops mismatches).
const LAN_DISCOVERY_OFFLINE_MS = 4000;
const LAN_DISCOVERY_ONLINE_MS = 20000;

function _startLanDiscoveryLoop() {
  if (STATE.lanDiscoveryTimer) return;
  let lastOnlinePass = 0;
  STATE.lanDiscoveryTimer = setInterval(async () => {
    if (STATE.destroyed || !STATE.armed) return;
    const socketOpen = !!(STATE.socket && STATE.socket.readyState === WebSocket.OPEN);
    const now = Date.now();
    if (socketOpen && now - lastOnlinePass < LAN_DISCOVERY_ONLINE_MS) return;
    if (socketOpen) lastOnlinePass = now;
    // Online: the cloud roster is authoritative — only add LIVE mDNS
    // peers (they are provably on this LAN right now). Offline: also
    // try peers we paired with recently (cache), they are likely still
    // in the building.
    try { await _lanDiscoveryPass({ includeCache: !socketOpen }); } catch (_) { /* best-effort */ }
  }, LAN_DISCOVERY_OFFLINE_MS);
}

function _stopLanDiscoveryLoop() {
  if (STATE.lanDiscoveryTimer) {
    try { clearInterval(STATE.lanDiscoveryTimer); } catch (_) { /* noop */ }
    STATE.lanDiscoveryTimer = null;
  }
}

async function _lanDiscoveryPass({ includeCache = true } = {}) {
  if (typeof window === "undefined" || !STATE.peerId) return;
  const candidates = new Map(); // device_id → { role, source }
  // (a) live mDNS results from the native layer (hosts / coordinators)
  try {
    const sigMod = await import("./lanSignalling");
    const signaller = sigMod.getSharedSignaller(STATE.peerId);
    if (!signaller.tauri && !signaller.capacitor) await signaller.detect();
    if (signaller.tauri || signaller.capacitor) {
      const peers = await signaller.discoveredPeers();
      for (const p of peers || []) {
        if (p && p.device_id && p.device_id !== STATE.peerId) {
          if (p.business_id && STATE.businessId && String(p.business_id) !== String(STATE.businessId)) continue;
          candidates.set(p.device_id, { role: p.role || null, source: "mdns" });
        }
      }
    }
  } catch (_) { /* noop */ }
  // (b) peers we paired with earlier (survives reloads; TTL-pruned)
  if (includeCache) try {
    const cache = await import("./lanPeerCache");
    for (const p of cache.listCachedLanPeers() || []) {
      if (p && p.device_id && p.device_id !== STATE.peerId && !candidates.has(p.device_id)) {
        if (p.business_id && STATE.businessId && String(p.business_id) !== String(STATE.businessId)) continue;
        candidates.set(p.device_id, { role: p.role || null, source: "cache" });
      }
    }
  } catch (_) { /* noop */ }
  if (candidates.size === 0) return;
  let changed = false;
  for (const [peerId, info] of candidates) {
    if (_peerAlreadyLive(peerId)) continue;
    _ensurePeer(peerId, info.role || undefined);
    changed = true;
    if (_shouldInitiate(peerId)) {
      try { await _makeOfferTo(peerId); } catch (e) { log("lan-discovery offer failed", peerId, e); }
    }
  }
  if (changed) _emitStatus();
}

function _bindChannel(peerId, peer, channel) {
  peer.channel = channel;
  peer.probeMisses = 0;
  channel.onopen = () => {
    peer.ready = true;
    peer.probeMisses = 0;
    peer.reofferCount = 0; // Session 30.26 — handshake watchdog counter
    log("channel open ->", peerId);
    // Session 27.73 — Introduce ourselves so the peer can render a
    // human-readable label for this device in its LAN status popover.
    // The message is one-shot and idempotent — receiving multiple
    // hellos just overwrites the stored label with the freshest value.
    try {
      channel.send(JSON.stringify({
        type: "hello",
        device_label: STATE.deviceLabel || null,
        role: STATE.role || null,
      }));
    } catch (_) { /* noop */ }
    // Session 27.77 — Capture the peer's LAN IPv4 from the
    // established WebRTC session and persist it. This gives us a
    // durable "known peer" list that survives page reloads and can
    // be used to short-circuit mDNS discovery on later reconnects.
    try {
      const pcRef = peer.pc;
      if (pcRef) {
        // Fire-and-forget so slow SDP parsing doesn't hold up the
        // channel-open path.
        Promise.resolve().then(() => {
          try {
            const { recordFromRtcPeerConnection } = require("./lanPeerCache");
            recordFromRtcPeerConnection(pcRef, {
              device_id: peerId,
              device_label: peer.deviceLabel || null,
              role: peer.role || null,
              business_id: STATE.businessId || null,
            });
          } catch (_) {
            // Fallback for environments without `require` — dynamic import.
            try {
              import("./lanPeerCache").then((m) => {
                m.recordFromRtcPeerConnection(pcRef, {
                  device_id: peerId,
                  device_label: peer.deviceLabel || null,
                  role: peer.role || null,
                  business_id: STATE.businessId || null,
                });
              });
            } catch (_) { /* noop */ }
          }
        });
      }
    } catch (_) { /* noop */ }
    _emitStatus();
    _startPeerProbe(peerId, peer);
  };
  channel.onclose = () => {
    peer.ready = false;
    _stopPeerProbe(peer);
    _emitStatus();
  };
  channel.onerror = () => { /* handled via onclose */ };
  channel.onmessage = (ev) => {
    let msg;
    try { msg = JSON.parse(ev.data); } catch (_) { return; }
    if (!msg || typeof msg.type !== "string") return;
    _handleChannelMessage(peerId, peer, msg);
  };
}

// Session 27.10 — Per-peer liveness probe.
// Sends a lightweight `ping` over the data channel every PEER_PROBE_MS.
// If we don't see a `pong` back within PEER_PROBE_TIMEOUT_MS we count
// a miss; two consecutive misses triggers an ICE restart. This is
// the mechanism that keeps the pill on "LAN" through short Wi-Fi
// blips instead of falling back to Cloud on the first hiccup.
function _startPeerProbe(peerId, peer) {
  _stopPeerProbe(peer);
  peer.probeTimer = setInterval(() => {
    if (!peer.channel || peer.channel.readyState !== "open") return;
    try {
      peer.channel.send(JSON.stringify({ type: "ping", ts: Date.now() }));
    } catch (_) { /* noop */ }
    peer.probeAwaitingSince = Date.now();
    // Timer that fires if we don't hear a pong in time.
    setTimeout(() => {
      if (!peer.probeAwaitingSince) return;
      if (Date.now() - peer.probeAwaitingSince < PEER_PROBE_TIMEOUT_MS) return;
      peer.probeMisses = (peer.probeMisses || 0) + 1;
      peer.probeAwaitingSince = 0;
      log("probe miss", peerId, "count", peer.probeMisses);
      if (peer.probeMisses >= 2) {
        _tryIceRestart(peerId, peer).catch(() => { /* noop */ });
      }
    }, PEER_PROBE_TIMEOUT_MS + 100);
  }, PEER_PROBE_MS);
}

function _stopPeerProbe(peer) {
  if (peer && peer.probeTimer) {
    try { clearInterval(peer.probeTimer); } catch (_) { /* noop */ }
    peer.probeTimer = null;
  }
  if (peer) {
    peer.probeAwaitingSince = 0;
  }
}

async function _tryIceRestart(peerId, peer) {
  if (!peer || !peer.pc) return;
  // Only the initiator can restart ICE cleanly (createOffer + send).
  // The receiver just waits for the fresh offer from the initiator.
  if (!peer.initiator) return;
  try {
    log("ICE restart ->", peerId);
    const offer = await peer.pc.createOffer({ iceRestart: true });
    await peer.pc.setLocalDescription(offer);
    peer.lastOfferAt = Date.now();
    _sendSignal({ type: "offer", to: peerId, sdp: peer.pc.localDescription.sdp });
    peer.probeMisses = 0;
  } catch (e) {
    log("ICE restart failed", peerId, e);
  }
}

// ─── Session 30.26 — Handshake watchdog ───────────────────────────
// Found by the router-only audit: an offer had NO retry. If the relay
// delivered it to a stale socket (the peer reconnected a moment
// earlier), or it was simply lost, the initiator sat in
// `have-local-offer` forever and the receiver in `new` forever — both
// "announced", neither paired, until someone logged out. Every 5 s:
//   • initiator, not connected, last offer > 8 s ago → re-offer (ICE
//     restart). After 4 attempts the RTCPeerConnection is recreated.
//   • receiver, no remote description after 12 s → send a
//     `renegotiate` nudge (relayed by the hub / LAN mailbox) so the
//     initiator re-offers immediately instead of waiting its turn.
const HANDSHAKE_TICK_MS = 5000;
const REOFFER_AFTER_MS = 8000;
const NUDGE_AFTER_MS = 12000;
const MAX_REOFFERS_BEFORE_RECREATE = 4;

function _startHandshakeWatchdog() {
  if (STATE.handshakeTimer) return;
  STATE.handshakeTimer = setInterval(() => { _handshakeTick().catch(() => {}); }, HANDSHAKE_TICK_MS);
}

function _stopHandshakeWatchdog() {
  if (STATE.handshakeTimer) {
    try { clearInterval(STATE.handshakeTimer); } catch (_) { /* noop */ }
    STATE.handshakeTimer = null;
  }
}

async function _handshakeTick() {
  if (STATE.destroyed || !STATE.armed) return;
  const now = Date.now();
  for (const [peerId, peer] of Array.from(STATE.peers.entries())) {
    if (!peer || !peer.pc || peer.ready) continue;
    const pcState = peer.pc.connectionState;
    if (pcState === "connected") continue; // channel still opening — give it time
    if (!peer.createdAt) peer.createdAt = now;
    const age = now - peer.createdAt;
    if (peer.initiator) {
      if (!peer.lastOfferAt) {
        // Never offered (e.g. peer_joined raced destroy/init) → offer now.
        try { await _makeOfferTo(peerId); } catch (_) { /* noop */ }
        continue;
      }
      if (now - peer.lastOfferAt < REOFFER_AFTER_MS) continue;
      peer.reofferCount = (peer.reofferCount || 0) + 1;
      if (peer.reofferCount > MAX_REOFFERS_BEFORE_RECREATE || pcState === "closed") {
        log("handshake watchdog: recreating pc ->", peerId);
        try { peer.channel && peer.channel.close(); } catch (_) { /* noop */ }
        try { peer.pc.close(); } catch (_) { /* noop */ }
        const role = peer.role;
        STATE.peers.delete(peerId);
        const fresh = _ensurePeer(peerId, role || undefined);
        fresh.createdAt = now;
        try { await _makeOfferTo(peerId); } catch (_) { /* noop */ }
        continue;
      }
      log("handshake watchdog: re-offer", peer.reofferCount, "->", peerId);
      await _tryIceRestart(peerId, peer);
    } else {
      // Receiver side: nothing to answer yet?
      const hasRemote = !!(peer.pc.remoteDescription && peer.pc.remoteDescription.type);
      if (hasRemote) continue;
      if (age < NUDGE_AFTER_MS) continue;
      if (peer.lastNudgeAt && now - peer.lastNudgeAt < NUDGE_AFTER_MS) continue;
      peer.lastNudgeAt = now;
      log("handshake watchdog: nudge initiator ->", peerId);
      _sendSignal({ type: "renegotiate", to: peerId });
    }
  }
}

// ─── in-band mesh protocol ────────────────────────────────────────
// Two message types travel over the data channel:
//   { type: "event", envelope, target_role }   ← application event
//   { type: "ack",   change_id }               ← receipt
function _handleChannelMessage(peerId, peer, msg) {
  // Session 27.10 — Liveness ping/pong on the data channel.
  if (msg.type === "ping") {
    try { peer.channel.send(JSON.stringify({ type: "pong", ts: msg.ts || Date.now() })); }
    catch (_) { /* noop */ }
    return;
  }
  if (msg.type === "pong") {
    peer.probeAwaitingSince = 0;
    peer.probeMisses = 0;
    return;
  }
  // Session 27.73 — Identity handshake. Store the peer's human-readable
  // label so the status popover can render "Windows PC · Owner" instead
  // of a raw UUID. Silently ignore duplicate hellos beyond overwriting
  // the label (peers may send a fresh hello after an ICE restart).
  if (msg.type === "hello") {
    if (typeof msg.device_label === "string" && msg.device_label.length > 0) {
      peer.deviceLabel = msg.device_label;
    }
    if (typeof msg.role === "string" && msg.role.length > 0) {
      peer.role = msg.role;
    }
    _emitStatus();
    return;
  }
  if (msg.type === "ack" && msg.change_id) {
    const pend = STATE.pending.get(msg.change_id);
    if (pend) {
      pend.resolve({ delivered: true, via: "lan", latencyMs: Date.now() - pend.startedAt, ackedBy: peerId });
      clearTimeout(pend.timer);
      STATE.pending.delete(msg.change_id);
    }
    return;
  }
  if (msg.type === "event" && msg.envelope) {
    const targetRole = String(msg.target_role || "*");
    const iAmTarget = targetRole === "*" || targetRole === STATE.role;
    if (iAmTarget) {
      // Deliver to our subscribers (they'll call applyRemoteEvent).
      STATE.eventHandlers.forEach((fn) => {
        try { fn(msg.envelope, { peer_id: peerId, role: peer.role, targetRole }); } catch (e) { console.warn("[lanMesh] event handler error:", e?.message || e); }
      });
      // ACK the sender.
      try { peer.channel.send(JSON.stringify({ type: "ack", change_id: msg.envelope.change_id })); } catch (_) { /* noop */ }
    }
    return;
  }
}

// ─── outbound send ────────────────────────────────────────────────
/**
 * Send an event envelope to peers matching `targetRole` via LAN.
 *
 * Resolves with `{ delivered, via, latencyMs, ackedBy? }`:
 *   • `delivered: true, via: "lan"` — at least one peer of the target
 *     role ACK'd within the timeout.
 *   • `delivered: false, via: "cloud"` — no peers eligible or nobody
 *     ACK'd in time; caller (the sync loop) is expected to route
 *     through the cloud instead.
 *
 * Never throws.
 */
export function sendMeshEvent(envelope, { targetRole = "*" } = {}) {
  return new Promise((resolve) => {
    if (!envelope || !envelope.change_id) {
      resolve({ delivered: false, via: "cloud", reason: "invalid-envelope" });
      return;
    }
    // Find eligible ready peers.
    const eligible = [];
    STATE.peers.forEach((p, id) => {
      if (!p.ready || !p.channel || p.channel.readyState !== "open") return;
      if (targetRole === "*" || p.role === targetRole) {
        eligible.push({ id, peer: p });
      }
    });
    if (eligible.length === 0) {
      resolve({ delivered: false, via: "cloud", reason: "no-peers" });
      return;
    }
    const startedAt = Date.now();
    const timer = setTimeout(() => {
      STATE.pending.delete(envelope.change_id);
      resolve({ delivered: false, via: "cloud", reason: "ack-timeout" });
    }, ACK_TIMEOUT_MS);
    STATE.pending.set(envelope.change_id, { resolve, timer, targetRole, startedAt });

    const payload = JSON.stringify({ type: "event", envelope, target_role: targetRole });
    for (const { peer } of eligible) {
      try { peer.channel.send(payload); } catch (_) { /* try the rest */ }
    }
  });
}

// Also handy for imperative code paths that don't want a Promise
// (e.g. "fire an announcement, don't wait").
export function fireAndForgetMesh(envelope, { targetRole = "*" } = {}) {
  sendMeshEvent(envelope, { targetRole }).catch(() => {});
}

// ─── diagnostic helpers ───────────────────────────────────────────
// Exposed on window so operators (and support) can inspect why a
// tablet is stuck in "Cloud only" without touching source code.
// Run `window.nfcsMeshStatus()` from the browser console to see the
// full state; `window.nfcsMeshDebug(true)` flips verbose logging.
export function dumpMeshDiagnostics() {
  const snap = getMeshStatus();
  const peers = [];
  STATE.peers.forEach((p, id) => {
    peers.push({
      peer_id: id,
      role: p.role,
      ready: p.ready,
      initiator: p.initiator,
      pc_state: p.pc ? p.pc.connectionState : "n/a",
      ice_state: p.pc ? p.pc.iceConnectionState : "n/a",
      signalling_state: p.pc ? p.pc.signalingState : "n/a",
      channel_state: p.channel ? p.channel.readyState : "n/a",
      pending_ice: p.pendingIce.length,
    });
  });
  return {
    status: snap.status,
    self: snap.self,
    business_id: STATE.businessId,
    signal_socket: STATE.socket ? {
      ready_state: STATE.socket.readyState,
      url: STATE.socket.url,
    } : null,
    signal_reconnect_attempts: STATE.socketReconnectAttempt,
    peer_count: peers.length,
    peers,
    pending_acks: STATE.pending.size,
  };
}

if (typeof window !== "undefined") {
  try {
    window.nfcsMeshStatus = () => {
      const diag = dumpMeshDiagnostics();
      // eslint-disable-next-line no-console
      console.table(diag.peers);
      // eslint-disable-next-line no-console
      console.log("[nfcsMeshStatus]", diag);
      return diag;
    };
    window.nfcsMeshDebug = (on) => {
      window.__NFCS_MESH_DEBUG = !!on;
      // eslint-disable-next-line no-console
      console.log(`[nfcsMeshDebug] verbose logging ${on ? "ENABLED" : "DISABLED"}`);
    };
  } catch (_) { /* noop */ }
}
